'use client';

import React, { useState, useRef } from 'react';

const MAX_SIZE = process.env.NEXT_PUBLIC_MAX_FILE_SIZE ? parseInt(process.env.NEXT_PUBLIC_MAX_FILE_SIZE) : 1048576; // 1 MB in bytes (default)

function StringReversalPage() {
    const [inputString, setInputString] = useState('');
    const [reversedString, setReversedString] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        if (newValue.length <= MAX_SIZE) {
            setInputString(newValue);
            setReversedString(reverseString(newValue));
            setError(null);
        } else {
            setError(`Input exceeds the maximum limit of ${MAX_SIZE / 1024} KB.`);
        }
    };

    const reverseString = (str: string) => {
        return str.split('').reverse().join('');
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setIsLoading(true);
            setError(null);

            if (!file.type.match('text.*') && !file.name.endsWith('.txt')) {
                setError('Please upload a text file (.txt)');
                setIsLoading(false);
                return;
            }

            // Check file size
            if (file.size > MAX_SIZE) {
                setError(`File size exceeds the maximum limit of ${MAX_SIZE / 1024} KB.`);
                setIsLoading(false);
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const content = e.target?.result as string;
                    if (content.length <= MAX_SIZE) {
                        setInputString(content);
                        setReversedString(reverseString(content));
                    } else {
                        setError(`File content exceeds the maximum limit of ${MAX_SIZE / 1024} KB.`);
                    }
                } catch (err) {
                    setError('Error reading file. Please try again.');
                }
                setIsLoading(false);
            };
            reader.onerror = () => {
                setError('Error reading file. Please try again.');
                setIsLoading(false);
            };
            reader.readAsText(file);
        }
    };

    const triggerFileUpload = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="w-full max-w-6xl mx-auto p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">String Reversal Tool</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Input */}
                <div className="w-full md:w-1/2 space-y-4">
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Enter your string (max {MAX_SIZE / 1024} KB):</span>
                        </label>
                        <textarea
                            className="textarea textarea-bordered h-96"
                            value={inputString}
                            onChange={handleInputChange}
                            placeholder="Enter text or upload a file"
                            maxLength={MAX_SIZE}
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <button
                            className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                            onClick={triggerFileUpload}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Uploading...' : 'Upload File'}
                        </button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            className="hidden"
                            accept=".txt,text/*"
                        />
                    </div>
                    {error && (
                        <div className='alert alert-error'>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 shrink-0 stroke-current"
                                fill="none"
                                viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                {/* Right Column - Output */}
                <div className="w-full md:w-1/2">
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Reversed string:</span>
                        </label>
                        <textarea
                            className="textarea textarea-bordered h-96"
                            value={reversedString}
                            readOnly
                            placeholder="Reversed text will appear here"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StringReversalPage;